import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, setToken } from '../lib/api';
import { track, EventTypes } from '../lib/analytics';

const AuthContext = createContext(null);

// Capacidad de perfil profesional individual. Deriva de role + plan_id;
// NUNCA cambia el role. Solo el plan 'pro' desbloquea el perfil profesional
// para un role='user'. Debe mantenerse en sync con backend/auth.py.
export function hasProfessionalAccess(user) {
  if (!user) return false;
  if (['agent', 'agency', 'admin'].includes(user.role)) return true;
  return user.plan_id === 'pro';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
    } catch {
      setUser(null);
      setToken(null);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('habita_token')) {
      fetchMe().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await api.login({ email, password });
    setToken(res.access_token);
    setUser(res.user);
    return res.user;
  };

  const register = async (payload) => {
    const res = await api.register(payload);
    setToken(res.access_token);
    setUser(res.user);
    // Fire signup_completed → mapped to Meta Pixel `CompleteRegistration`,
    // GA4 `signup_completed`, PostHog & Clarity. Not sent to backend (no
    // dot in event name → skips /events/track which is whitelist-only).
    try {
      track(EventTypes.SIGNUP_COMPLETED, {
        role: res.user?.role || 'user',
        method: 'email',
      });
    } catch (err) { void err; }
    return res.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    window.location.href = '/';
  };

  const refresh = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser, isProfessional: hasProfessionalAccess(user) }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
